
import React from "react";
import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  X, 
  Mail, 
  Users, 
  Trophy, 
  LineChart, 
  Search, 
  Briefcase, 
  ShieldCheck, 
  Clock, 
  Zap
} from "lucide-react";

const EnterpriseTalent = () => {
  return (
    <>
      <Helmet>
        <title>Enterprise Talent Solutions | DGK Freelance Hub</title>
        <meta 
          name="description" 
          content="Scale your workforce with on-demand access to top talent. Our enterprise solutions help you find, hire and manage the best professionals for your business needs."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6">
                Enterprise Talent Solutions
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Access vetted professionals on demand. Scale your workforce with quality talent for your most important projects.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-6 text-lg rounded-lg shadow-lg">
                  Request a Demo
                </Button>
                <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50 px-6 py-6 text-lg rounded-lg">
                  Talk to Sales
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-2 text-gray-600">
                <CheckCircle2 className="text-green-500 h-5 w-5" />
                <span>No commitment required</span>
              </div>
            </div>
            <div className="lg:pl-10">
              <Card className="overflow-hidden border-0 shadow-2xl">
                <img 
                  src="/lovable-uploads/37636e5b-5cb0-43f5-9669-c7f4811cc3cb.png" 
                  alt="Enterprise talent dashboard" 
                  className="w-full h-auto object-cover rounded-t-lg"
                />
                <CardContent className="bg-white p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Talent Dashboard</h3>
                  <p className="text-gray-600 text-sm">
                    Our intuitive platform helps you find, hire, and manage top freelance professionals.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Key Benefits Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Key Benefits for Enterprises
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our enterprise solutions are designed to help businesses access top talent quickly and efficiently.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Users className="h-8 w-8 text-blue-500" />,
                title: "Talent Pool Access",
                description: "Get immediate access to our pre-vetted network of skilled professionals across various disciplines."
              },
              {
                icon: <Trophy className="h-8 w-8 text-indigo-500" />,
                title: "Quality Guarantees",
                description: "All freelancers undergo rigorous skill assessments to ensure they meet your quality standards."
              },
              {
                icon: <LineChart className="h-8 w-8 text-purple-500" />,
                title: "Scalable Solutions",
                description: "Easily scale your team up or down based on your business needs and project requirements."
              },
              {
                icon: <Search className="h-8 w-8 text-green-500" />,
                title: "AI-Powered Matching",
                description: "Our advanced algorithms match your requirements with the most suitable talent."
              },
              {
                icon: <Briefcase className="h-8 w-8 text-amber-500" />,
                title: "Project Management",
                description: "Integrated tools to help you manage freelancers and track project progress efficiently."
              },
              {
                icon: <ShieldCheck className="h-8 w-8 text-red-500" />,
                title: "Enterprise Security",
                description: "Industry-leading security measures to protect your intellectual property and data."
              }
            ].map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="bg-blue-50 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-white rounded-2xl shadow-sm">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A simplified process to help you find and hire the right talent for your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                icon: <Briefcase className="h-8 w-8 text-blue-500" />,
                title: "Define Your Needs",
                description: "Share your requirements with our enterprise team. We'll help you identify the right skills and experience needed."
              },
              {
                step: "02",
                icon: <Search className="h-8 w-8 text-blue-500" />,
                title: "Match with Talent",
                description: "Our AI-powered matching system identifies the most suitable professionals from our global talent pool."
              },
              {
                step: "03",
                icon: <CheckCircle2 className="h-8 w-8 text-blue-500" />,
                title: "Collaborate & Scale",
                description: "Seamlessly onboard talent and manage projects through our secure platform with dedicated support."
              }
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-blue-50 rounded-xl p-6 h-full">
                  <div className="absolute top-4 right-4 bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">
                    {step.step}
                  </div>
                  <div className="mb-4">{step.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 text-blue-300">
                    <svg width="40" height="12" xmlns="http://www.w3.org/2000/svg">
                      <path d="M40 6H0M34 1L40 6 34 11" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Trusted by Leading Companies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what our enterprise clients have to say about our talent solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              {
                quote: "DGK's enterprise talent solutions have transformed how we scale our development team. The quality of professionals is consistently high.",
                author: "Sarah Johnson",
                role: "CTO, TechInnovate",
                company: "TechInnovate"
              },
              {
                quote: "We've reduced our hiring time by 60% while improving the quality of our project outcomes by working with DGK's enterprise solutions.",
                author: "Michael Chen",
                role: "VP of Engineering, DataSphere",
                company: "DataSphere"
              },
              {
                quote: "The security and compliance features of DGK's enterprise platform gave us the confidence to embrace remote talent at scale.",
                author: "Emily Rodriguez",
                role: "CISO, FinSecure",
                company: "FinSecure"
              }
            ].map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg p-6">
                <CardContent className="pt-0 px-0 pb-0">
                  <div className="text-4xl text-blue-200 mb-4">"</div>
                  <p className="text-gray-700 mb-6">{testimonial.quote}</p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.author.charAt(0)}
                    </div>
                    <div className="ml-4">
                      <p className="font-semibold text-gray-900">{testimonial.author}</p>
                      <p className="text-gray-500 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Enterprise Features */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Enterprise-Grade Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our platform is built with enterprise needs in mind, offering robust capabilities for large organizations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <ShieldCheck className="h-6 w-6 text-blue-500" />,
                title: "Enhanced Security",
                description: "SOC 2 and GDPR compliant platform with encryption at rest and in transit."
              },
              {
                icon: <Users className="h-6 w-6 text-blue-500" />,
                title: "Dedicated Account Team",
                description: "Work with a consistent team that understands your business needs."
              },
              {
                icon: <Clock className="h-6 w-6 text-blue-500" />,
                title: "Fast Talent Matching",
                description: "Get matched with qualified candidates within 48 hours."
              },
              {
                icon: <LineChart className="h-6 w-6 text-blue-500" />,
                title: "Performance Analytics",
                description: "Track project progress, spend, and team performance in real-time."
              },
              {
                icon: <Zap className="h-6 w-6 text-blue-500" />,
                title: "Custom Workflows",
                description: "Tailor the platform to match your internal processes and requirements."
              },
              {
                icon: <Briefcase className="h-6 w-6 text-blue-500" />,
                title: "Workforce Planning",
                description: "Strategic tools to plan your talent needs for upcoming projects."
              },
              {
                icon: <CheckCircle2 className="h-6 w-6 text-blue-500" />,
                title: "Quality Assurance",
                description: "Rigorous pre-screening and ongoing performance monitoring."
              },
              {
                icon: <X className="h-6 w-6 text-blue-500" />,
                title: "Contract Management",
                description: "Streamlined process for handling contracts, NDAs, and other legal documents."
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white p-5 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="bg-blue-50 rounded-full p-3 w-12 h-12 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-16">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 md:p-12 shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Ready to transform your talent strategy?
                </h2>
                <p className="text-blue-100 text-lg mb-8">
                  Talk to our enterprise team today and discover how our talent solutions can help your business scale efficiently.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button className="bg-white text-blue-700 hover:bg-blue-50 px-6 py-6 text-lg rounded-lg shadow-lg">
                    Request a Demo
                  </Button>
                  <Button variant="outline" className="border-white text-white hover:bg-blue-700 px-6 py-6 text-lg rounded-lg">
                    <Mail className="mr-2 h-5 w-5" />
                    Contact Sales
                  </Button>
                </div>
              </div>
              <div className="lg:text-right hidden lg:block">
                <img 
                  src="/lovable-uploads/a3a4afe9-fb10-41da-8ba6-5ded5f9f40be.png" 
                  alt="Enterprise dashboard" 
                  className="inline-block max-w-full rounded-lg shadow-2xl" 
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default EnterpriseTalent;
